import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronLeft, Sparkles, Building, LogIn, FileText, Upload,
  CheckCircle2, Send, MessageCircle, ClipboardList, Users, Trophy, ScrollText, Wrench,
  Award, Star, Bell, CreditCard, RotateCcw, Download
} from 'lucide-react';

// =================================================================
// BUYER LIFECYCLE — Housekeeping AMC for 102 Flats
// =================================================================
const BUYER_LIFECYCLE = [
  {
    title: 'Buyer Login',
    icon: LogIn,
    headline: 'Welcome, Green Heights Co-operative Housing Society',
    body: [
      ['Organization', 'Green Heights Co-operative Housing Society'],
      ['Registered Address', '102 Flats · Manpada, Thane West, Maharashtra 400607'],
      ['Membership', 'Founding Member · 24 months validity · Active'],
      ['Logged in as', 'Rakesh Sharma — Honorary Secretary'],
    ],
    cta: 'Open Dashboard →',
  },
  {
    title: 'Create Requirement',
    icon: FileText,
    headline: 'New Requirement — Auto-populated for "Housekeeping AMC"',
    body: [
      ['Category', 'Housekeeping AMC'],
      ['Property', '102 Flats — 12-storey residential tower'],
      ['Estimated Value', '₹ 8,50,000'],
      ['Contract Duration', '12 Months'],
      ['Service Area', 'Mumbai, Maharashtra'],
      ['Mode of Engagement', 'Single Vendor — Full Outsourcing'],
    ],
    cta: 'Proceed to Tender Details →',
  },
  {
    title: 'Tender Details — Auto-Filled',
    icon: ClipboardList,
    headline: 'A complete tender brief prepared in under 60 seconds',
    body: [
      ['Scope of Work', 'Daily housekeeping of all common areas: lobbies, staircases, lifts, basements, terrace, garden walkways, society office, gym, clubhouse.'],
      ['Manpower Requirement', '8 housekeeping staff + 1 supervisor (rotational shifts 7:00 AM – 9:00 PM)'],
      ['Cleaning Schedule', 'Daily sweeping & mopping · Weekly deep cleaning · Monthly facade & glass cleaning'],
      ['Consumables', 'All cleaning chemicals, dusters, brooms, garbage bags — supplied by vendor'],
      ['Reporting Structure', 'Daily checklist signed by Building Manager · Weekly review with Secretary'],
      ['Performance Standards', 'ISO 9001 cleaning SOPs · Zero complaints policy · 2-hr response on escalations'],
      ['Service Timings', '7:00 AM – 9:00 PM, Mon–Sun (rotational off)'],
      ['Payment Terms', 'Monthly billing · Payment within 7 working days of receipt'],
      ['Penalty Clauses', '5% deduction on unmet daily checklists · 10% on safety lapses'],
    ],
    note: 'No EMD required for this tender — direct quotation submission.',
    cta: 'Send for Internal Approval →',
  },
  {
    title: 'Internal Approval Workflow',
    icon: CheckCircle2,
    headline: 'Society governance documents tracked & approved',
    workflow: [
      { state: 'Draft Created', date: '02 Jun 2026', actor: 'Secretary', status: 'done' },
      { state: 'Committee Resolution Uploaded', date: '04 Jun 2026', actor: 'Secretary', status: 'done', doc: 'GHCHS_Committee_Resolution_2026.pdf' },
      { state: 'Chairman Approval Received', date: '05 Jun 2026', actor: 'Chairman', status: 'done', doc: 'Chairman_Approval_Letter.pdf' },
      { state: 'Budget Approval — Finance Committee', date: '05 Jun 2026', actor: 'Treasurer', status: 'done', doc: 'Budget_Sanction_FY26.pdf' },
      { state: 'Approval Letter Generated', date: '06 Jun 2026', actor: 'System', status: 'done', doc: 'Internal_Approval_AMC2026.pdf' },
      { state: 'Approved for Publication ✓', date: '06 Jun 2026', actor: 'Secretary', status: 'final' },
    ],
    cta: 'Publish Tender →',
  },
  {
    title: 'Publish Tender',
    icon: Send,
    headline: 'Tender is now live on ProcureOne Ultra',
    body: [
      ['Tender Number', 'PUI-TND-2026-0428'],
      ['Tender Title', 'Housekeeping AMC for 102 Flats — Green Heights CHS'],
      ['Publish Date', '06 Jun 2026, 14:30 IST'],
      ['Clarification Window', '07 Jun – 13 Jun 2026'],
      ['Quotation Closing Date', '20 Jun 2026, 18:00 IST'],
      ['Visibility', 'Verified Housekeeping Suppliers in Mumbai region (43 matched)'],
    ],
    note: '✓ All matching suppliers notified via email + dashboard alert.',
    cta: 'Open Clarifications Window →',
  },
  {
    title: 'Supplier Clarification Stage',
    icon: MessageCircle,
    headline: 'Suppliers ask. You answer. Everyone stays aligned.',
    qa: [
      { from: 'Supplier A — Prime Facility Services', q: 'Is GST extra on the quoted amount or inclusive?', a: 'GST is extra. Quoted amount should be excluding GST.' },
      { from: 'Supplier B — Sunshine Facility Pvt. Ltd.', q: 'Is night-shift housekeeping required?', a: 'No. Service hours are 7:00 AM – 9:00 PM only.' },
      { from: 'Supplier C — Coastal Cleaning Co.', q: 'Are consumables billed separately?', a: 'No. All consumables are included in your monthly quotation.' },
      { from: 'Supplier D — Skyline Services', q: 'Do we need to provide uniforms?', a: 'Yes. Vendor must provide branded uniforms + ID cards for all staff.' },
    ],
    note: 'Clarification Period Closed — 13 Jun 2026',
    cta: 'View Submitted Quotations →',
  },
  {
    title: 'Quotation Submission',
    icon: ClipboardList,
    headline: '3 Suppliers Submitted Quotations',
    quotes: [
      { supplier: 'Supplier A — Prime Facility Services', amount: '₹ 7,95,000', timeline: '15 days mobilization', rating: 4.8 },
      { supplier: 'Supplier B — Sunshine Facility Pvt. Ltd.', amount: '₹ 8,10,000', timeline: '10 days mobilization', rating: 4.5 },
      { supplier: 'Supplier C — Coastal Cleaning Co.', amount: '₹ 8,25,000', timeline: '7 days mobilization', rating: 4.2 },
    ],
    note: 'Quotation Submission Closed — 20 Jun 2026',
    cta: 'Generate Comparative Statement →',
  },
  {
    title: 'Comparative Statement',
    icon: Users,
    headline: 'Side-by-side comparison auto-generated',
    table: {
      headers: ['Parameter', 'Supplier A', 'Supplier B', 'Supplier C'],
      rows: [
        ['Quoted Price (₹)', '7,95,000', '8,10,000', '8,25,000'],
        ['Mobilization Timeline', '15 days', '10 days', '7 days'],
        ['Years of Experience', '12 years', '9 years', '15 years'],
        ['Past Society Clients', '38', '24', '52'],
        ['Average Rating', '★ 4.8', '★ 4.5', '★ 4.2'],
        ['ISO Certified', 'Yes', 'Yes', 'No'],
        ['Documents Complete', '✓', '✓', '✓'],
      ],
    },
    note: '📥 Download Comparative Statement (PDF)',
    cta: 'Send to Committee for Review →',
  },
  {
    title: 'Committee Review',
    icon: Users,
    headline: 'Committee evaluates and recommends',
    workflow: [
      { state: 'Comparative Report Generated', date: '21 Jun 2026', actor: 'System', status: 'done' },
      { state: 'Circulated to Committee Members', date: '22 Jun 2026', actor: 'Secretary', status: 'done', doc: 'Committee_Circulation.pdf' },
      { state: 'Committee Meeting Held', date: '24 Jun 2026', actor: 'All Members', status: 'done', doc: 'MoM_Committee_Meeting.pdf' },
      { state: 'Vendor Recommended: Supplier A — Prime Facility Services', date: '24 Jun 2026', actor: 'Committee', status: 'final' },
    ],
    cta: 'Proceed with Award →',
  },
  {
    title: 'Award Process',
    icon: Trophy,
    headline: 'Awarded Supplier: Prime Facility Services',
    body: [
      ['Award Date', '25 Jun 2026'],
      ['Awarded Amount', '₹ 7,95,000 + 18% GST'],
      ['Award Approval', 'Chairman + Secretary jointly signed'],
      ['Award Letter Generated', 'PUI_Award_Letter_PrimeFS_2026.pdf'],
      ['Visibility', 'All 3 participating suppliers notified of outcome (winner + losers)'],
    ],
    note: '✓ Limited award details (winner + value) visible to all participants for transparency.',
    cta: 'Issue Purchase Order →',
  },
  {
    title: 'Purchase Order',
    icon: ScrollText,
    headline: 'PO Issued — Contract Activated',
    body: [
      ['PO Number', 'GHCHS-PO-2026-0079'],
      ['Awarded Vendor', 'Prime Facility Services Pvt. Ltd.'],
      ['Contract Start Date', '01 Jul 2026'],
      ['Contract End Date', '30 Jun 2027'],
      ['Contract Value', '₹ 7,95,000 + GST (per annum)'],
      ['Payment Terms', 'Monthly billing — within 7 working days'],
      ['Penalty Clauses', '5% on unmet checklists · 10% on safety lapses'],
    ],
    note: '📥 PO + Signed Contract auto-stored in society document vault.',
    cta: 'Track Contract Execution →',
  },
  {
    title: 'Contract Completion',
    icon: Wrench,
    headline: '12-month contract executed successfully',
    workflow: [
      { state: 'Monthly Service Reports Received', date: 'Jul 2026 – Jun 2027', actor: 'Vendor', status: 'done', doc: '12 Monthly Reports' },
      { state: 'Quarterly Audits Completed', date: '4 quarters', actor: 'Society Manager', status: 'done', doc: 'Audit_Reports_Q1-Q4.pdf' },
      { state: 'NOC Uploaded', date: '28 Jun 2027', actor: 'Secretary', status: 'done', doc: 'NOC_Prime_FS.pdf' },
      { state: 'Supplier Completion Certificate', date: '29 Jun 2027', actor: 'Vendor', status: 'done', doc: 'Completion_Cert_PrimeFS.pdf' },
      { state: 'Final Payment Released', date: '30 Jun 2027', actor: 'Treasurer', status: 'done', doc: 'Final_Settlement_INR_7_95_000.pdf' },
      { state: 'Tender Closed ✓', date: '30 Jun 2027', actor: 'System', status: 'final' },
    ],
    note: '🌟 Society rated supplier 5/5 · Supplier rated society 4.8/5 (two-way feedback complete).',
    cta: 'Demo Complete →',
  },
];

// =================================================================
// SUPPLIER LIFECYCLE
// =================================================================
const SUPPLIER_LIFECYCLE = [
  { title: 'Supplier Login', icon: LogIn, headline: 'Welcome, Prime Facility Services Pvt. Ltd.', body: [['Organization', 'Prime Facility Services Pvt. Ltd.'], ['Category', 'Facility Management · Housekeeping'], ['Service Region', 'Mumbai · Thane · Navi Mumbai'], ['Membership', 'Founding Supplier · 24 months validity']], cta: 'Open Dashboard →' },
  { title: 'Active Tender Listed', icon: FileText, headline: 'New matching opportunity', body: [['Tender No.', 'PUI-TND-2026-0428'], ['Title', 'Housekeeping AMC for 102 Flats'], ['Buyer', 'Green Heights CHS, Mumbai'], ['Estimated Value', '₹ 8,50,000'], ['Closing Date', '20 Jun 2026']], cta: 'View Tender Details →' },
  { title: 'Tender Details', icon: ClipboardList, headline: 'Complete tender brief downloaded (FREE)', body: [['Scope', '8 housekeeping staff + 1 supervisor · 102 flats'], ['Service Hours', '7:00 AM – 9:00 PM, Mon–Sun'], ['Documents', 'BOQ, Scope, Penalty Clauses (all downloaded)'], ['Eligibility', 'ISO certified · 5+ yrs society experience']], cta: 'Raise Clarification →' },
  { title: 'Clarification Raised', icon: MessageCircle, headline: 'Question submitted to buyer', qa: [{ from: 'You — Prime Facility Services', q: 'Is GST extra on the quoted amount or inclusive?', a: 'Buyer reply: GST is extra. Quoted amount excluding GST.' }], cta: 'Submit Quotation →' },
  { title: 'Quotation Submitted', icon: Send, headline: 'Bid submitted successfully', body: [['Your Quote', '₹ 7,95,000 + GST'], ['Mobilization', '15 days'], ['Warranty / SLA', '2-hour response on escalations'], ['Attachments', 'Company Profile · ISO Certificate · 5 client references'], ['Participation Fee', '₹ 2,500 + GST (auto-billed)']], note: 'Bid auto-moved from "Saved" to "My Participated Tenders".', cta: 'Track Evaluation →' },
  { title: 'Under Evaluation', icon: ClipboardList, headline: 'Buyer is reviewing all submissions', body: [['Status', 'Under Committee Review'], ['Suppliers Submitted', '3 (you are one)'], ['Expected Decision', 'Within 5 working days']], cta: 'Wait for Notification →' },
  { title: 'Award Notification', icon: Bell, headline: '🎉 Congratulations — You Won!', body: [['Award Date', '25 Jun 2026'], ['Award Value', '₹ 7,95,000 + GST'], ['Buyer', 'Green Heights CHS'], ['Notification', 'Email + SMS + Dashboard alert received']], note: 'Post-award charge: ₹ 2,385 (0.3% of award value) auto-invoiced.', cta: 'Download Purchase Order →' },
  { title: 'Purchase Order Received', icon: ScrollText, headline: 'PO downloaded and contract activated', body: [['PO Number', 'GHCHS-PO-2026-0079'], ['Contract Start', '01 Jul 2026'], ['Contract Value', '₹ 7,95,000 + GST per annum'], ['Payment Terms', 'Monthly billing · 7 working days']], cta: 'Begin Work Execution →' },
  { title: 'Work Completion', icon: Wrench, headline: '12-month service contract executed', body: [['Monthly Reports', '12 / 12 submitted on time'], ['Quarterly Audits', '4 / 4 passed'], ['NOC Issued', '28 Jun 2027 by Society Secretary'], ['Completion Certificate', 'Uploaded to platform']], cta: 'Receive Final Payment →' },
  { title: 'Payment Received · Closed', icon: CreditCard, headline: 'Contract Closed Successfully', body: [['Final Settlement', '₹ 7,95,000 received 30 Jun 2027'], ['Buyer Feedback', '★ 5.0 — "Exceptional service"'], ['Your Rating of Buyer', '★ 4.8'], ['Status', 'Closed ✓']], note: '🏆 Your supplier profile rating improved to 4.85/5 — visible to future buyers.', cta: 'Demo Complete →' },
];

// =================================================================
// MAIN COMPONENT
// =================================================================
const LiveDemoLifecycle = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const isBuyer = role === 'buyer';
  const flow = isBuyer ? BUYER_LIFECYCLE : SUPPLIER_LIFECYCLE;
  const total = flow.length;
  const current = flow[step];
  const Icon = current.icon;
  const progressPct = Math.round(((step + 1) / total) * 100);
  const accent = isBuyer ? 'navy' : 'orange';

  const isLast = step === total - 1;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-orange text-white text-center py-1.5 sm:py-2 text-xs sm:text-sm font-semibold uppercase tracking-wider">
        Live Demo · Procurement Lifecycle Simulation · No Real Data Affected
      </div>
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => navigate('/demo')} className="text-white/70 hover:text-white inline-flex items-center text-sm" data-testid="lifecycle-back-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Exit Demo
          </button>
          <div className="flex items-center gap-2 text-sm sm:text-base font-semibold">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-gold" />
            {isBuyer ? 'Buyer Procurement Lifecycle' : 'Supplier Lifecycle'}
          </div>
          <div className="text-xs text-white/60 hidden sm:block">{isBuyer ? 'Green Heights CHS' : 'Prime Facility Services'}</div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 pt-5 sm:pt-7 pb-2 max-w-4xl">
        <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
          <div className="text-xs sm:text-sm font-semibold text-navy" data-testid="lifecycle-step-counter">
            Step <span className={`text-${accent}`}>{step + 1}</span> of {total}
          </div>
          <div className="text-xs text-muted-foreground">{progressPct}% complete</div>
        </div>
        <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-gradient-to-r from-gold to-orange transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-10 max-w-4xl">
        <div className="bg-card border border-border rounded-lg p-5 sm:p-8 shadow-sm" data-testid={`lifecycle-step-${step}`}>
          <div className="flex items-start gap-4 mb-5">
            <div className={`p-3 rounded-lg ${isBuyer ? 'bg-navy text-white' : 'bg-orange text-white'} flex-shrink-0`}>
              <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Step {step + 1} of {total}</div>
              <h2 className="text-xl sm:text-2xl font-bold text-navy">{current.title}</h2>
              <p className="text-sm text-muted-foreground mt-1">{current.headline}</p>
            </div>
          </div>

          {/* Body — key-value table */}
          {current.body && (
            <div className="space-y-2 mb-5">
              {current.body.map(([k, v], i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-1 sm:gap-3 py-2 border-b border-border/40 last:border-0 text-sm">
                  <div className="text-muted-foreground sm:w-44 flex-shrink-0">{k}</div>
                  <div className="text-foreground font-medium flex-1">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Workflow timeline */}
          {current.workflow && (
            <ol className="space-y-2 mb-5">
              {current.workflow.map((w, i) => (
                <li key={i} className="flex items-start gap-3 p-3 rounded border border-border bg-muted/20" data-testid={`workflow-${i}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${w.status === 'final' ? 'bg-green-600 text-white' : 'bg-gold text-navy'}`}>
                    {w.status === 'final' ? '✓' : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${w.status === 'final' ? 'text-green-700' : 'text-navy'}`}>{w.state}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{w.date} · {w.actor}{w.doc ? <> · 📎 <span className="font-mono">{w.doc}</span></> : null}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {/* Q&A clarifications */}
          {current.qa && (
            <div className="space-y-3 mb-5">
              {current.qa.map((c, i) => (
                <div key={i} className="p-3 rounded border border-border bg-muted/20" data-testid={`qa-${i}`}>
                  <div className="text-xs text-orange font-semibold mb-1">{c.from}</div>
                  <div className="text-sm font-medium text-navy mb-1">Q: {c.q}</div>
                  <div className="text-sm text-foreground/80">A: {c.a}</div>
                </div>
              ))}
            </div>
          )}

          {/* Quotation list */}
          {current.quotes && (
            <div className="space-y-2 mb-5">
              {current.quotes.map((q, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded border border-border bg-muted/20" data-testid={`quote-${i}`}>
                  <div>
                    <div className="text-sm font-semibold text-navy">{q.supplier}</div>
                    <div className="text-xs text-muted-foreground">{q.timeline} · <span className="text-orange font-semibold">★ {q.rating}</span></div>
                  </div>
                  <div className="text-lg font-bold text-navy">{q.amount}</div>
                </div>
              ))}
            </div>
          )}

          {/* Comparison table */}
          {current.table && (
            <div className="overflow-x-auto mb-5">
              <table className="w-full text-xs sm:text-sm border border-border" data-testid="comparison-table">
                <thead className="bg-navy text-white">
                  <tr>{current.table.headers.map((h, i) => <th key={i} className="py-2 px-3 text-left font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {current.table.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border odd:bg-muted/20">
                      {r.map((c, j) => <td key={j} className={`py-2 px-3 ${j === 0 ? 'text-muted-foreground' : 'font-medium'}`}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {current.note && (
            <div className="mt-4 p-3 rounded bg-gold/10 border border-gold/30 text-sm text-navy">
              {current.note}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-5 sm:mt-7">
          <button onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0} className="px-4 py-2 rounded border border-input text-sm font-semibold inline-flex items-center disabled:opacity-30" data-testid="lifecycle-prev-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Previous
          </button>
          <div className="text-xs text-muted-foreground hidden sm:block">{step + 1} / {total}</div>
          {isLast ? (
            <button onClick={() => navigate(isBuyer ? '/buyer-register' : '/supplier-register')} className={`px-5 py-2 rounded text-sm font-bold inline-flex items-center bg-gold hover:bg-orange text-navy hover:text-white`} data-testid="lifecycle-cta-register">
              Register as {isBuyer ? 'Buyer' : 'Supplier'} <ArrowRight className="h-4 w-4 ml-2" />
            </button>
          ) : (
            <button onClick={() => setStep(step + 1)} className={`px-5 py-2 rounded text-sm font-bold inline-flex items-center ${isBuyer ? 'bg-navy hover:bg-navy/90' : 'bg-orange hover:bg-orange/90'} text-white`} data-testid="lifecycle-next-btn">
              {current.cta || 'Next'} <ArrowRight className="h-4 w-4 ml-2" />
            </button>
          )}
        </div>

        {/* Quick jump dots */}
        <div className="flex justify-center mt-5 sm:mt-7 gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {flow.map((_, i) => (
            <button key={i} onClick={() => setStep(i)} aria-label={`Step ${i + 1}`} className={`w-2 h-2 rounded-full transition-all flex-shrink-0 ${i === step ? (isBuyer ? 'bg-navy w-6' : 'bg-orange w-6') : i < step ? 'bg-gold' : 'bg-muted'}`} />
          ))}
        </div>

        {step > 0 && (
          <div className="text-center mt-5">
            <button onClick={() => setStep(0)} className="text-xs text-muted-foreground hover:text-navy inline-flex items-center">
              <RotateCcw className="h-3 w-3 mr-1" /> Restart Lifecycle
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default LiveDemoLifecycle;
